import {useCallback, useMemo, useState} from 'react';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import useClickOutside from '~hooks/useClickOutside';
import {PlotListFilters} from '~types/Plots';
import {Order, useFilters} from '~providers/FiltersProvider';

export type SelectItem = Pick<PlotListFilters, 'sortBy' | 'sortDirection'> & {
    label: string;
    selected: boolean;
};

export default function useSelect() {
    const [open, setOpen] = useState(false);
    const t = useScopeTranslation('list', 'select');
    const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
    const {
        filters: {sortDirection, sortBy},
        setFilter,
    } = useFilters();
    const options = useMemo((): SelectItem[] => {
        const item = (
            label: string,
            sB: SelectItem['sortBy'],
            sD: SelectItem['sortDirection'],
        ) => ({
            selected: sD === sortDirection && sB === sortBy,
            sortDirection: sD,
            sortBy: sB,
            label,
        });

        return [
            item(t('priceToHigh'), 'price', Order.ASC),
            item(t('priceToLow'), 'price', Order.DESC),
        ];
    }, [t, sortDirection, sortBy]);

    const onSelect = useCallback((item: SelectItem) => {
        setFilter('sortDirection', item.sortDirection);
        setFilter('sortBy', item.sortBy);
        setOpen(false);
    }, []);

    return {
        ref,
        open,
        onSelect,
        selectedLabel: useMemo(
            () => options.find((o) => o.selected)?.label,
            [options],
        ),
        options,
        toggleOpen: () => setOpen(!open),
    };
}
